import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Play,
  Camera,
  Copy,
  Check,
  RefreshCw,
  Pencil,
  Monitor,
  Loader2,
  Square,
  Layers,
  Plus,
  Trash2,
  Users,
  Send,
  X,
} from 'lucide-react';
import { api } from '../api/client';
import type {
  ExecutionWithAction,
  CaptureSessionWithAction,
  ExecutionLogEntry,
  CaptureLogEntry,
  ExecutionStatus,
  ExecutionTier,
} from '@databro/shared';
import RunActionModal from '../components/RunActionModal';
import ActionForm from '../components/ActionForm';
import BatchDetails from '../components/BatchDetails';
import {
  subscribeToCaptureStarted,
  subscribeToCaptureLog,
  subscribeToCaptureFailed,
  subscribeToCaptureCompleted,
} from '../hooks/useWebSocket';
import {
  Button,
  Badge,
  StatusBadge,
  Skeleton,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  ScrollArea,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Checkbox,
  Label,
} from '@databro/ui';
import {
  useActionDetailStore,
  useActivities,
  useActiveCapture,
  useActiveExecution,
  useHasActiveCapture,
  useHasActiveExecution,
  useHasCredentials,
  useBoundClients,
  useCaptureLogs,
  useIsActivityLive,
  mapCaptureStatusForDisplay,
  type Activity,
} from '../stores/useActionDetailStore';

export default function ActionDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  // Client management state
  const [clientsModalOpen, setClientsModalOpen] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);

  // Execution selection state for bulk actions
  const [selectedExecutionIds, setSelectedExecutionIds] = useState<Set<string>>(new Set());

  // Get store state and actions - destructure to get stable references
  const {
    action: storeAction,
    selectedActivity,
    isActionLoading,
    isExecutionsLoading,
    isCapturesLoading,
    runModalOpen,
    editModalOpen,
    errorDialog,
    clients: storeClients,
    isCapturing,
    isRunning,
    isStoppingCapture,
    isCancellingExecution,
    setActionId,
    setAction,
    setActionLoading,
    setExecutions,
    setExecutionsLoading,
    setCaptures,
    setCapturesLoading,
    setClients,
    setSelectedActivity,
    setRunModalOpen,
    setEditModalOpen,
    setErrorDialog,
    closeErrorDialog,
    showError,
    setCapturing,
    setRunning,
    setStoppingCapture,
    setCancellingExecution,
    initRealtimeLogs,
    addRealtimeLog,
    reset,
  } = useActionDetailStore();

  const activities = useActivities();
  const activeCapture = useActiveCapture();
  const activeExecution = useActiveExecution();
  const hasActiveCapture = useHasActiveCapture();
  const hasActiveExecution = useHasActiveExecution();
  const hasCredentials = useHasCredentials();
  const boundClients = useBoundClients();

  // Sync route param to store and reset on unmount
  useEffect(() => {
    setActionId(id ?? null);
    return () => reset();
  }, [id, setActionId, reset]);

  // Fetch action details
  const { data: action, isLoading: actionLoading } = useQuery({
    queryKey: ['action', id],
    queryFn: () => api.getAction(id!),
    enabled: !!id,
  });

  // Sync action to store
  useEffect(() => {
    setAction(action ?? null);
    setActionLoading(actionLoading);
  }, [action, actionLoading, setAction, setActionLoading]);

  // Fetch executions for this action
  const { data: executionsData, isLoading: executionsLoading } = useQuery({
    queryKey: ['executions', { actionId: id }],
    queryFn: () => api.getExecutions({ actionId: id, limit: 50 }),
    enabled: !!id,
  });

  // Sync executions to store
  useEffect(() => {
    setExecutions(executionsData?.data ?? []);
    setExecutionsLoading(executionsLoading);
  }, [executionsData, executionsLoading, setExecutions, setExecutionsLoading]);

  // Fetch capture sessions for this action
  const { data: capturesData, isLoading: capturesLoading } = useQuery({
    queryKey: ['captures', { actionId: id }],
    queryFn: () => api.getCaptureSessions({ actionId: id, limit: 50 }),
    enabled: !!id,
  });

  // Sync captures to store
  useEffect(() => {
    setCaptures(capturesData?.data ?? []);
    setCapturesLoading(capturesLoading);
  }, [capturesData, capturesLoading, setCaptures, setCapturesLoading]);

  // Fetch clients for edit form
  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.getClients(),
  });

  // Sync clients to store
  useEffect(() => {
    setClients(clients ?? []);
  }, [clients, setClients]);

  // Auto-select first activity (latest) if none selected
  useEffect(() => {
    if (!selectedActivity && activities.length > 0) {
      // Select the first activity which is the latest (sorted by createdAt desc)
      setSelectedActivity(activities[0]);
    }
  }, [activities, selectedActivity, setSelectedActivity]);

  // Subscribe to WebSocket events for real-time updates
  useEffect(() => {
    if (!id) return;

    const unsubStarted = subscribeToCaptureStarted((actionId, captureSessionId) => {
      if (actionId === id) {
        queryClient.invalidateQueries({ queryKey: ['captures', { actionId: id }] });
        initRealtimeLogs(captureSessionId);
      }
    });

    const unsubLog = subscribeToCaptureLog((actionId, captureSessionId, log) => {
      if (actionId === id) {
        addRealtimeLog(captureSessionId, log);
      }
    });

    const unsubCompleted = subscribeToCaptureCompleted((actionId, _captureSessionId) => {
      if (actionId === id) {
        queryClient.invalidateQueries({ queryKey: ['captures', { actionId: id }] });
        queryClient.invalidateQueries({ queryKey: ['actions'] });
        queryClient.invalidateQueries({ queryKey: ['action', id] });
      }
    });

    const unsubFailed = subscribeToCaptureFailed((actionId, _captureSessionId, _error) => {
      if (actionId === id) {
        queryClient.invalidateQueries({ queryKey: ['captures', { actionId: id }] });
      }
    });

    return () => {
      unsubStarted();
      unsubLog();
      unsubCompleted();
      unsubFailed();
    };
  }, [id, queryClient, initRealtimeLogs, addRealtimeLog]);

  // Capture mutation
  const captureMutation = useMutation({
    mutationFn: () => api.triggerCapture(id!),
    onMutate: () => setCapturing(true),
    onSettled: () => setCapturing(false),
    onError: (err) => {
      showError('Capture Failed', err instanceof Error ? err.message : 'Failed to trigger capture');
    },
  });

  // Run mutation
  const runMutation = useMutation({
    mutationFn: (inputs?: Record<string, unknown>) => api.runAction(id!, inputs),
    onMutate: () => setRunning(true),
    onSettled: () => setRunning(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executions', { actionId: id }] });
    },
    onError: (err) => {
      showError('Run Failed', err instanceof Error ? err.message : 'Failed to run action');
    },
  });

  // Stop capture mutation
  const stopCaptureMutation = useMutation({
    mutationFn: (captureId: string) => api.stopCapture(captureId),
    onMutate: () => setStoppingCapture(true),
    onSettled: () => setStoppingCapture(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['captures', { actionId: id }] });
    },
    onError: (err) => {
      showError('Stop Capture Failed', err instanceof Error ? err.message : 'Failed to stop capture');
    },
  });

  // Cancel execution mutation
  const cancelExecutionMutation = useMutation({
    mutationFn: (executionId: string) => api.cancelExecution(executionId),
    onMutate: () => setCancellingExecution(true),
    onSettled: () => setCancellingExecution(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executions', { actionId: id }] });
    },
    onError: (err) => {
      showError('Cancel Failed', err instanceof Error ? err.message : 'Failed to cancel execution');
    },
  });

  // Assign clients mutation
  const assignClientsMutation = useMutation({
    mutationFn: (clientIds: string[]) => api.assignClientsToAction(id!, clientIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action', id] });
      queryClient.invalidateQueries({ queryKey: ['actions'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setClientsModalOpen(false);
    },
    onError: (err) => {
      showError('Assignment Failed', err instanceof Error ? err.message : 'Failed to assign clients');
    },
  });

  // Remove client mutation
  const removeClientMutation = useMutation({
    mutationFn: (clientId: string) => api.removeClientFromAction(id!, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action', id] });
      queryClient.invalidateQueries({ queryKey: ['actions'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (err) => {
      showError('Remove Failed', err instanceof Error ? err.message : 'Failed to remove client');
    },
  });

  // Retry webhooks mutation
  const retryWebhooksMutation = useMutation({
    mutationFn: (executionIds: string[]) => api.retryWebhooks(executionIds),
    onSuccess: (result) => {
      alert(`Webhooks queued: ${result.queued}, skipped: ${result.skipped}`);
      setSelectedExecutionIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['executions', { actionId: id }] });
    },
    onError: (err) => {
      showError('Retry Webhooks Failed', err instanceof Error ? err.message : 'Failed to retry webhooks');
    },
  });

  // Retry executions mutation
  const retryExecutionsMutation = useMutation({
    mutationFn: (executionIds: string[]) => api.retryExecutions(executionIds),
    onSuccess: (result) => {
      alert(`Executions queued: ${result.queued}, skipped: ${result.skipped}`);
      setSelectedExecutionIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['executions', { actionId: id }] });
    },
    onError: (err) => {
      showError('Retry Executions Failed', err instanceof Error ? err.message : 'Failed to retry executions');
    },
  });

  // Toggle execution selection
  const handleToggleExecution = useCallback((executionId: string) => {
    setSelectedExecutionIds((prev) => {
      const next = new Set(prev);
      if (next.has(executionId)) {
        next.delete(executionId);
      } else {
        next.add(executionId);
      }
      return next;
    });
  }, []);

  // Clear selection
  const handleClearSelection = useCallback(() => {
    setSelectedExecutionIds(new Set());
  }, []);

  // Handle retry webhooks
  const handleRetryWebhooks = useCallback(() => {
    if (selectedExecutionIds.size > 0) {
      retryWebhooksMutation.mutate(Array.from(selectedExecutionIds));
    }
  }, [selectedExecutionIds, retryWebhooksMutation]);

  // Handle retry executions
  const handleRetryExecutions = useCallback(() => {
    if (selectedExecutionIds.size > 0) {
      retryExecutionsMutation.mutate(Array.from(selectedExecutionIds));
    }
  }, [selectedExecutionIds, retryExecutionsMutation]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['executions', { actionId: id }] });
    queryClient.invalidateQueries({ queryKey: ['captures', { actionId: id }] });
  };

  const handleOpenClientsModal = () => {
    setSelectedClientIds(boundClients.map((c) => c.id));
    setClientsModalOpen(true);
  };

  const handleToggleClient = (clientId: string) => {
    setSelectedClientIds((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleAssignClients = () => {
    const currentIds = boundClients.map((c) => c.id);
    const toAdd = selectedClientIds.filter((id) => !currentIds.includes(id));
    if (toAdd.length > 0) {
      assignClientsMutation.mutate(toAdd);
    } else {
      setClientsModalOpen(false);
    }
  };

  // Get available clients (not yet assigned)
  const assignedClientIds = boundClients.map((c) => c.id);
  const availableClients = storeClients.filter((c) => !assignedClientIds.includes(c.id));

  if (isActionLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!storeAction) {
    return (
      <div className="space-y-4">
        <Link to="/actions" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          Back to Actions
        </Link>
        <div className="border rounded-lg py-8 text-center">
          <p className="text-muted-foreground">Action not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-56px-48px)]">
      {/* Header */}
      <div className="flex items-start justify-between flex-shrink-0 pb-4">
        <div>
          <Link to="/actions" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Actions
          </Link>
          <h1 className="text-2xl font-bold">{storeAction.name}</h1>
          <div className="flex items-center gap-2 mt-1 text-muted-foreground text-sm">
            <Badge variant="outline">{storeAction.platform}</Badge>
            <span>•</span>
            <span>{storeAction.scheduleType === 'periodic' && storeAction.intervalMs
              ? `Every ${formatInterval(storeAction.intervalMs)}`
              : 'Manual'}</span>
            <span>•</span>
            <button
              onClick={handleOpenClientsModal}
              className="inline-flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer"
            >
              <Users className="h-3 w-3" />
              {boundClients.length === 0
                ? 'No clients'
                : boundClients.length === 1
                  ? boundClients[0].name || boundClients[0].id.slice(0, 8)
                  : `${boundClients.length} clients`}
            </button>
            {!hasCredentials && (
              <>
                <span>•</span>
                <Badge variant="destructive">Needs capture</Badge>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setEditModalOpen(true)}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>

          {/* Capture Button - unified state */}
          <CaptureButton
            hasActiveCapture={hasActiveCapture}
            activeCapture={activeCapture}
            hasCredentials={hasCredentials}
            isCapturing={isCapturing}
            isStoppingCapture={isStoppingCapture}
            onCapture={() => captureMutation.mutate()}
            onStopCapture={(captureId) => stopCaptureMutation.mutate(captureId)}
          />

          {/* Run Button - unified state */}
          <RunButton
            hasActiveExecution={hasActiveExecution}
            activeExecution={activeExecution}
            hasCredentials={hasCredentials}
            isRunning={isRunning}
            isCancellingExecution={isCancellingExecution}
            onRun={() => setRunModalOpen(true)}
            onCancelExecution={(executionId) => cancelExecutionMutation.mutate(executionId)}
          />
        </div>
      </div>

      {/* Activity Section */}
      <div className="flex border-t overflow-hidden -mx-4 md:-mx-6 -mb-4 md:-mb-6 flex-1 min-h-0">
        {/* Left: Activity List */}
        <div className="w-[360px] flex-shrink-0 flex flex-col border-r bg-background">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <span className="font-semibold text-sm">Activity</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleRefresh}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          {/* Bulk action bar */}
          {selectedExecutionIds.size > 0 && (
            <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/50">
              <span className="text-sm text-muted-foreground">
                {selectedExecutionIds.size} selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryExecutions}
                  disabled={retryExecutionsMutation.isPending}
                >
                  {retryExecutionsMutation.isPending ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-1" />
                  )}
                  Retry Executions
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryWebhooks}
                  disabled={retryWebhooksMutation.isPending}
                >
                  {retryWebhooksMutation.isPending ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Send className="h-3 w-3 mr-1" />
                  )}
                  Retry Webhooks
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {(isExecutionsLoading || isCapturesLoading) && (
              <div className="p-4 space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            )}
            {!isExecutionsLoading && !isCapturesLoading && activities.length === 0 && (
              <div className="p-4 text-center text-muted-foreground">
                No activity yet. Run or capture to get started.
              </div>
            )}
            {activities.map((activity) => (
              <ActivityListItem
                key={`${activity.type}-${activity.id}`}
                activity={activity}
                isSelected={selectedActivity?.id === activity.id && selectedActivity?.type === activity.type}
                isChecked={activity.type === 'execution' && selectedExecutionIds.has(activity.id)}
                onCheckChange={activity.type === 'execution' ? () => handleToggleExecution(activity.id) : undefined}
                onClick={() => setSelectedActivity(activity)}
              />
            ))}
          </div>
        </div>

        {/* Right: Activity Details */}
        <div className="flex-1 flex flex-col bg-background min-h-0">
          <div className="px-4 py-3 border-b flex-shrink-0">
            <span className="font-semibold text-sm">
              {selectedActivity
                ? selectedActivity.type === 'execution'
                  ? 'Execution Details'
                  : selectedActivity.type === 'batch'
                    ? 'Batch Details'
                    : 'Capture Details'
                : 'Details'}
            </span>
          </div>
          {!selectedActivity && (
            <div className="text-center text-muted-foreground py-8">
              Select an activity to view details
            </div>
          )}
          {selectedActivity?.type === 'execution' && selectedActivity.execution && (
            <div className="flex-1 flex flex-col min-h-0 px-4 pt-4">
              <ExecutionDetails execution={selectedActivity.execution} />
            </div>
          )}
          {selectedActivity?.type === 'batch' && selectedActivity.batchId && (
            <div className="flex-1 flex flex-col min-h-0">
              <BatchDetails
                batchId={selectedActivity.batchId}
                onExecutionSelect={(executionId) => {
                  // Find the execution in batchExecutions and select it
                  const exec = selectedActivity.batchExecutions?.find((e) => e.id === executionId);
                  if (exec) {
                    setSelectedActivity({
                      id: exec.id,
                      type: 'execution',
                      status: exec.status,
                      createdAt: exec.createdAt,
                      completedAt: exec.completedAt,
                      error: exec.error,
                      execution: exec,
                    });
                  }
                }}
              />
            </div>
          )}
          {selectedActivity?.type === 'capture' && selectedActivity.capture && (
            <div className="flex-1 flex flex-col min-h-0 px-4 pt-4">
              <CaptureDetails capture={selectedActivity.capture} />
            </div>
          )}
        </div>
      </div>

      {/* Run Modal */}
      {runModalOpen && storeAction && (
        <RunActionModal
          action={storeAction as any}
          onClose={() => setRunModalOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['executions', { actionId: id }] });
            setRunModalOpen(false);
          }}
        />
      )}

      {/* Edit Modal */}
      {editModalOpen && storeAction && (
        <ActionForm
          action={storeAction as any}
          clients={storeClients}
          onClose={() => setEditModalOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['action', id] });
            queryClient.invalidateQueries({ queryKey: ['actions'] });
            setEditModalOpen(false);
          }}
        />
      )}

      {/* Clients Modal */}
      <Dialog open={clientsModalOpen} onOpenChange={setClientsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Clients</DialogTitle>
            <DialogDescription>
              Assign clients to execute this action. Executions will be distributed in round-robin among online clients.
            </DialogDescription>
          </DialogHeader>

          {/* Currently assigned clients */}
          {boundClients.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Assigned Clients</Label>
              <div className="space-y-2 max-h-[150px] overflow-y-auto">
                {boundClients.map((client) => (
                  <div
                    key={client.id}
                    className="flex items-center justify-between p-2 border rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          client.isOnline ? 'bg-green-500' : 'bg-muted-foreground/30'
                        }`}
                      />
                      <Link
                        to={`/clients/${client.id}`}
                        className="font-medium text-sm hover:underline"
                        onClick={() => setClientsModalOpen(false)}
                      >
                        {client.name || client.id.slice(0, 8)}
                      </Link>
                      <Badge variant="secondary" className="text-xs">
                        {client.isOnline ? 'Online' : 'Offline'}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-destructive hover:text-destructive"
                      onClick={() => {
                        if (confirm('Remove this client from the action?')) {
                          removeClientMutation.mutate(client.id);
                        }
                      }}
                      disabled={removeClientMutation.isPending}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available clients to add */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {boundClients.length > 0 ? 'Add More Clients' : 'Available Clients'}
            </Label>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {availableClients.length === 0 && storeClients.length === 0 && (
                <div className="text-center text-muted-foreground py-4 text-sm">
                  No clients available. Register a client first.
                </div>
              )}
              {availableClients.length === 0 && storeClients.length > 0 && (
                <div className="text-center text-muted-foreground py-4 text-sm">
                  All clients are already assigned.
                </div>
              )}
              {availableClients.map((client) => (
                <div
                  key={client.id}
                  className="flex items-center gap-3 p-2 border rounded-lg hover:bg-muted/50 cursor-pointer"
                  onClick={() => handleToggleClient(client.id)}
                >
                  <Checkbox
                    id={`client-${client.id}`}
                    checked={selectedClientIds.includes(client.id)}
                    onCheckedChange={() => handleToggleClient(client.id)}
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        client.isOnline ? 'bg-green-500' : 'bg-muted-foreground/30'
                      }`}
                    />
                    <Label
                      htmlFor={`client-${client.id}`}
                      className="font-medium text-sm cursor-pointer"
                    >
                      {client.name || client.id.slice(0, 8)}
                    </Label>
                    <Badge variant="secondary" className="text-xs">
                      {client.isOnline ? 'Online' : 'Offline'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setClientsModalOpen(false);
                setSelectedClientIds([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignClients}
              disabled={
                assignClientsMutation.isPending ||
                selectedClientIds.filter((id) => !assignedClientIds.includes(id)).length === 0
              }
            >
              {assignClientsMutation.isPending ? 'Assigning...' : 'Assign Selected'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <AlertDialog open={errorDialog.open} onOpenChange={(open: boolean) => setErrorDialog({ ...errorDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{errorDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>{errorDialog.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => closeErrorDialog()}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Capture Button Component - uses unified state
function CaptureButton({
  hasActiveCapture,
  activeCapture,
  hasCredentials,
  isCapturing,
  isStoppingCapture,
  onCapture,
  onStopCapture,
}: {
  hasActiveCapture: boolean;
  activeCapture: CaptureSessionWithAction | null;
  hasCredentials: boolean;
  isCapturing: boolean;
  isStoppingCapture: boolean;
  onCapture: () => void;
  onStopCapture: (captureId: string) => void;
}) {
  // Show "Capturing..." button if there's an active capture OR if we just triggered one
  const showActiveState = hasActiveCapture || isCapturing;

  if (showActiveState) {
    return (
      <Button
        variant="outline"
        onClick={() => activeCapture && onStopCapture(activeCapture.id)}
        disabled={isStoppingCapture || !activeCapture}
        className="group hover:text-destructive hover:border-destructive/50 hover:bg-destructive/10"
      >
        {isStoppingCapture ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin group-hover:hidden" />
            <Square className="h-4 w-4 mr-2 hidden group-hover:block" />
          </>
        )}
        <span className="group-hover:hidden">Capturing...</span>
        <span className="hidden group-hover:inline">Stop Capture</span>
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      onClick={onCapture}
      disabled={isCapturing}
    >
      {isCapturing ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Camera className="h-4 w-4 mr-2" />
      )}
      {hasCredentials ? 'Re-capture' : 'Capture'}
    </Button>
  );
}

// Run Button Component - uses unified state
function RunButton({
  hasActiveExecution,
  activeExecution,
  hasCredentials,
  isRunning,
  isCancellingExecution,
  onRun,
  onCancelExecution,
}: {
  hasActiveExecution: boolean;
  activeExecution: ExecutionWithAction | null;
  hasCredentials: boolean;
  isRunning: boolean;
  isCancellingExecution: boolean;
  onRun: () => void;
  onCancelExecution: (executionId: string) => void;
}) {
  // Show "Running..." button if there's an active execution OR if we just triggered one
  const showActiveState = hasActiveExecution || isRunning;

  if (showActiveState) {
    return (
      <Button
        variant="outline"
        onClick={() => activeExecution && onCancelExecution(activeExecution.id)}
        disabled={isCancellingExecution || !activeExecution}
        className="group hover:text-destructive hover:border-destructive/50 hover:bg-destructive/10"
      >
        {isCancellingExecution ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin group-hover:hidden" />
            <Square className="h-4 w-4 mr-2 hidden group-hover:block" />
          </>
        )}
        <span className="group-hover:hidden">Running...</span>
        <span className="hidden group-hover:inline">Stop Run</span>
      </Button>
    );
  }

  return (
    <Button
      onClick={onRun}
      disabled={!hasCredentials || isRunning}
    >
      {isRunning ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Play className="h-4 w-4 mr-2" />
      )}
      Run
    </Button>
  );
}

// Activity List Item Component - uses unified state
function ActivityListItem({
  activity,
  isSelected,
  isChecked,
  onCheckChange,
  onClick,
}: {
  activity: Activity;
  isSelected: boolean;
  isChecked?: boolean;
  onCheckChange?: () => void;
  onClick: () => void;
}) {
  const isLive = useIsActivityLive(activity.id, activity.type);

  // Handle batch activity
  if (activity.type === 'batch') {
    const batchStatusColors: Record<string, string> = {
      running: 'bg-blue-500',
      completed: 'bg-green-500',
      partial: 'bg-yellow-500',
      failed: 'bg-red-500',
    };

    return (
      <div
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 ${
          isSelected ? 'bg-muted border-l-2 border-l-primary' : ''
        }`}
        onClick={onClick}
      >
        <div className="flex-shrink-0">
          <Layers className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">Batch</span>
            <Badge className={`text-xs ${batchStatusColors[activity.status] || 'bg-muted'}`}>
              {activity.status}
            </Badge>
            {activity.status === 'running' && (
              <Badge variant="outline" className="animate-pulse text-xs">Live</Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {new Date(activity.createdAt).toLocaleString()}
            <span className="ml-2">
              • {activity.batchCompleted}/{activity.batchTotal} done
              {activity.batchFailed ? ` • ${activity.batchFailed} failed` : ''}
            </span>
          </div>
        </div>
      </div>
    );
  }

  const displayStatus = activity.type === 'capture'
    ? mapCaptureStatusForDisplay(activity.status as any)
    : activity.status;

  // Check if activity is running
  const isRunning = activity.type === 'execution'
    ? ['pending', 'queued', 'running', 'waiting_for_client'].includes(activity.status)
    : ['pending', 'capturing'].includes(activity.status);

  // Format execution inputs for subtitle
  const getExecutionSubtitle = () => {
    if (activity.type !== 'execution' || !activity.execution) {
      return null;
    }

    const inputs = activity.execution.customInputs;
    if (inputs && Object.keys(inputs).length > 0) {
      // Format inputs as key=value pairs, converting timestamps to readable format
      const formatted = Object.entries(inputs)
        .map(([key, value]) => `${key}=${formatInputValue(key, value)}`)
        .join(', ');
      return formatted.length > 80 ? formatted.slice(0, 77) + '...' : formatted;
    }

    return null;
  };

  // Format time as HH:mm:ss
  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour12: false });
  };

  // Format date as MM/DD
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}`;
  };

  // Get execution timestamps
  const getTimestamps = () => {
    const createdAt = activity.createdAt;
    const completedAt = activity.type === 'execution'
      ? activity.execution?.completedAt
      : activity.capture?.completedAt;

    return { createdAt, completedAt };
  };

  const { createdAt, completedAt } = getTimestamps();
  const subtitle = getExecutionSubtitle();

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 ${
        isSelected ? 'bg-muted border-l-2 border-l-primary' : ''
      } ${isChecked ? 'bg-primary/5' : ''}`}
      onClick={onClick}
    >
      {/* Checkbox for executions */}
      {onCheckChange && (
        <div
          className="flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onCheckChange();
          }}
        >
          <Checkbox checked={isChecked} onCheckedChange={onCheckChange} />
        </div>
      )}
      <div className="flex-shrink-0">
        {isRunning ? (
          <Loader2 className="h-4 w-4 text-primary animate-spin" />
        ) : activity.type === 'execution' ? (
          <Play className="h-4 w-4 text-muted-foreground" />
        ) : (
          <Camera className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <div className="flex-grow min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm capitalize">{activity.type}</span>
          <StatusBadge status={displayStatus as ExecutionStatus | 'idle'} />
          {isLive && (
            <Badge variant="outline" className="animate-pulse text-xs">Live</Badge>
          )}
        </div>
        {subtitle && (
          <div className="text-xs text-muted-foreground truncate" title={subtitle}>
            {subtitle}
          </div>
        )}
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <span title={new Date(createdAt).toLocaleString()}>
            {formatDate(createdAt)} {formatTime(createdAt)}
          </span>
          {completedAt && (
            <>
              <span>→</span>
              <span title={new Date(completedAt).toLocaleString()}>
                {formatTime(completedAt)}
              </span>
            </>
          )}
          {activity.execution?.trigger && activity.type === 'execution' && (
            <span className="text-muted-foreground/70">• {activity.execution.trigger}</span>
          )}
        </div>
        {activity.error && (
          <div className="text-xs text-destructive truncate mt-1">{activity.error}</div>
        )}
      </div>
    </div>
  );
}

// Execution Waterfall Component - Shows tier-based execution timeline
interface TierData {
  tier: ExecutionTier;
  startTime: number;
  endTime: number;
  requests: {
    id: string;
    name: string;
    startTime: number;
    endTime: number;
    duration: number;
    status?: number;
    success: boolean;
  }[];
  success: boolean;
  error?: string;
}

function ExecutionWaterfall({
  logs,
  execution,
}: {
  logs: ExecutionLogEntry[] | undefined;
  execution: ExecutionWithAction;
}) {
  const waterfallData = useMemo(() => {
    if (!logs || logs.length === 0) return null;

    const executionStartTime = logs[0] ? new Date(logs[0].timestamp).getTime() : 0;
    const executionEndTime = logs[logs.length - 1]
      ? new Date(logs[logs.length - 1].timestamp).getTime()
      : executionStartTime;
    const totalDuration = executionEndTime - executionStartTime;

    // Group logs by tier
    const tierMap = new Map<ExecutionTier, TierData>();
    const tierOrder: ExecutionTier[] = ['server', 'worker', 'browser'];

    // Track request start times for duration calculation
    const requestStarts = new Map<string, { time: number; name: string }>();

    for (const log of logs) {
      if (!log.tier) continue;

      if (!tierMap.has(log.tier)) {
        tierMap.set(log.tier, {
          tier: log.tier,
          startTime: new Date(log.timestamp).getTime(),
          endTime: new Date(log.timestamp).getTime(),
          requests: [],
          success: true,
        });
      }

      const tierData = tierMap.get(log.tier)!;
      const logTime = new Date(log.timestamp).getTime();
      tierData.endTime = Math.max(tierData.endTime, logTime);

      // Track request lifecycle
      if (log.stage === 'request_started' && log.requestId) {
        requestStarts.set(log.requestId, {
          time: logTime,
          name: log.requestName || log.requestId,
        });
      }

      if ((log.stage === 'request_completed' || log.stage === 'request_failed') && log.requestId) {
        const startInfo = requestStarts.get(log.requestId);
        if (startInfo) {
          tierData.requests.push({
            id: log.requestId,
            name: startInfo.name,
            startTime: startInfo.time,
            endTime: logTime,
            duration: log.duration || logTime - startInfo.time,
            status: log.status,
            success: log.stage === 'request_completed',
          });
        }
      }

      if (log.stage === 'tier_failed') {
        tierData.success = false;
        tierData.error = log.error || log.message;
      }
    }

    return {
      tiers: tierOrder.filter((t) => tierMap.has(t)).map((t) => tierMap.get(t)!),
      totalDuration,
      executionStartTime,
    };
  }, [logs]);

  if (!waterfallData || waterfallData.tiers.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-8">
        No tier execution data available
      </div>
    );
  }

  const { tiers, totalDuration, executionStartTime } = waterfallData;

  const getTierColor = (tier: ExecutionTier, success: boolean) => {
    if (!success) return 'bg-destructive/20 border-destructive';
    switch (tier) {
      case 'server':
        return 'bg-blue-500/20 border-blue-500';
      case 'worker':
        return 'bg-purple-500/20 border-purple-500';
      case 'browser':
        return 'bg-orange-500/20 border-orange-500';
      default:
        return 'bg-muted border-border';
    }
  };

  const getRequestColor = (success: boolean, status?: number) => {
    if (!success) return 'bg-destructive';
    if (status && status >= 200 && status < 300) return 'bg-green-500';
    if (status && status >= 400) return 'bg-destructive';
    return 'bg-primary';
  };

  const getTierLabel = (tier: ExecutionTier) => {
    switch (tier) {
      case 'server':
        return 'Server';
      case 'worker':
        return 'Worker';
      case 'browser':
        return 'Browser';
      default:
        return tier;
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-6">
        {/* Summary */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Total Duration:</span>
            <Badge variant="outline">{formatDuration(totalDuration)}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Tiers Used:</span>
            <Badge variant="outline">{tiers.length}</Badge>
          </div>
        </div>

        {/* Tier Legend */}
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-500/50" />
            <span>Server</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-purple-500/50" />
            <span>Worker</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-orange-500/50" />
            <span>Browser</span>
          </div>
        </div>

        {/* Waterfall Chart */}
        <div className="space-y-4">
          {tiers.map((tierData, tierIndex) => {
            const tierStart = tierData.startTime - executionStartTime;
            const tierDuration = tierData.endTime - tierData.startTime;
            const tierLeftPercent = totalDuration > 0 ? (tierStart / totalDuration) * 100 : 0;
            const tierWidthPercent = totalDuration > 0 ? (tierDuration / totalDuration) * 100 : 100;

            return (
              <div key={tierData.tier} className="space-y-2">
                {/* Tier Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`${
                        tierData.tier === 'server'
                          ? 'border-blue-500 text-blue-500'
                          : tierData.tier === 'worker'
                            ? 'border-purple-500 text-purple-500'
                            : 'border-orange-500 text-orange-500'
                      }`}
                    >
                      {getTierLabel(tierData.tier)}
                    </Badge>
                    {!tierData.success && (
                      <Badge variant="destructive" className="text-xs">
                        Failed
                      </Badge>
                    )}
                    {tierData.success && tierIndex === tiers.length - 1 && (
                      <Badge variant="success" className="text-xs">
                        Success
                      </Badge>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDuration(tierDuration)} ({tierData.requests.length} request
                    {tierData.requests.length !== 1 ? 's' : ''})
                  </span>
                </div>

                {/* Tier Timeline Bar */}
                <div className="relative h-8 bg-muted rounded-md overflow-hidden">
                  {/* Tier background bar */}
                  <div
                    className={`absolute top-0 bottom-0 rounded border ${getTierColor(tierData.tier, tierData.success)}`}
                    style={{
                      left: `${tierLeftPercent}%`,
                      width: `${Math.max(tierWidthPercent, 1)}%`,
                    }}
                  />

                  {/* Request bars within tier */}
                  {tierData.requests.map((req) => {
                    const reqStart = req.startTime - executionStartTime;
                    const reqLeftPercent = totalDuration > 0 ? (reqStart / totalDuration) * 100 : 0;
                    const reqWidthPercent =
                      totalDuration > 0 ? (req.duration / totalDuration) * 100 : 5;

                    return (
                      <div
                        key={req.id}
                        className={`absolute top-1 bottom-1 rounded ${getRequestColor(req.success, req.status)} opacity-80 hover:opacity-100 cursor-pointer transition-opacity`}
                        style={{
                          left: `${reqLeftPercent}%`,
                          width: `${Math.max(reqWidthPercent, 0.5)}%`,
                          minWidth: '4px',
                        }}
                        title={`${req.name}: ${formatDuration(req.duration)}${req.status ? ` (${req.status})` : ''}`}
                      />
                    );
                  })}
                </div>

                {/* Request Details */}
                {tierData.requests.length > 0 && (
                  <div className="ml-4 space-y-1">
                    {tierData.requests.map((req) => (
                      <div
                        key={req.id}
                        className="flex items-center gap-2 text-xs text-muted-foreground"
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${getRequestColor(req.success, req.status)}`}
                        />
                        <span className="font-mono truncate max-w-[200px]" title={req.name}>
                          {req.name}
                        </span>
                        <span className="text-muted-foreground">{formatDuration(req.duration)}</span>
                        {req.status && (
                          <Badge
                            variant={req.status >= 200 && req.status < 300 ? 'success' : 'destructive'}
                            className="text-xs h-4"
                          >
                            {req.status}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Tier Error */}
                {tierData.error && (
                  <div className="ml-4 text-xs text-destructive bg-destructive/10 p-2 rounded">
                    {tierData.error}
                  </div>
                )}

                {/* Fallback indicator */}
                {!tierData.success && tierIndex < tiers.length - 1 && (
                  <div className="flex items-center gap-2 ml-4 text-xs text-yellow-500">
                    <span>↓</span>
                    <span>Falling back to {getTierLabel(tiers[tierIndex + 1].tier)}...</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Time Scale */}
        <div className="relative h-6 border-t pt-1">
          <div className="absolute left-0 text-xs text-muted-foreground">0ms</div>
          <div className="absolute left-1/4 text-xs text-muted-foreground">
            {formatDuration(totalDuration * 0.25)}
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 text-xs text-muted-foreground">
            {formatDuration(totalDuration * 0.5)}
          </div>
          <div className="absolute left-3/4 text-xs text-muted-foreground">
            {formatDuration(totalDuration * 0.75)}
          </div>
          <div className="absolute right-0 text-xs text-muted-foreground">
            {formatDuration(totalDuration)}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}

// Execution Details Component
function ExecutionDetails({ execution }: { execution: ExecutionWithAction }) {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('timeline');
  const [selectedLogIndex, setSelectedLogIndex] = useState<number | null>(null);
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const logs = execution.logs as ExecutionLogEntry[] | undefined;
  const selectedLog = selectedLogIndex !== null && logs ? logs[selectedLogIndex] : null;

  const handleCopyResults = async () => {
    if (execution.requestResults) {
      await navigator.clipboard.writeText(JSON.stringify(execution.requestResults, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Reset selection when execution changes
  useEffect(() => {
    setSelectedLogIndex(null);
  }, [execution.id]);

  const getStageIcon = (stage: string) => {
    if (stage.includes('completed')) return '✓';
    if (stage.includes('failed')) return '✕';
    if (stage.includes('fallback')) return '↓';
    if (stage.includes('started')) return '●';
    return '○';
  };

  const getStageColor = (stage: string) => {
    if (stage.includes('completed')) return 'text-green-500';
    if (stage.includes('failed')) return 'text-destructive';
    if (stage.includes('fallback')) return 'text-yellow-500';
    if (stage.includes('started')) return 'text-primary';
    return 'text-muted-foreground';
  };

  // Format timestamp as MM/DD HH:mm:ss
  const formatTimestamp = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const date = `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}`;
    const time = d.toLocaleTimeString('en-US', { hour12: false });
    return `${date} ${time}`;
  };

  return (
    <div className="flex flex-col h-full -mx-4 -mt-4">
      {/* Metadata */}
      <div className="grid grid-cols-2 gap-3 text-sm flex-shrink-0 px-4 pt-4 pb-4">
        <div>
          <div className="text-muted-foreground">Status</div>
          <StatusBadge status={execution.status} />
        </div>
        <div>
          <div className="text-muted-foreground">Trigger</div>
          <div>{execution.trigger}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Executor</div>
          <div>{execution.executor || '-'}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Duration</div>
          <div>
            {execution.completedAt && execution.startedAt
              ? `${Math.round((new Date(execution.completedAt).getTime() - new Date(execution.startedAt).getTime()) / 1000)}s`
              : '-'}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground">Started</div>
          <div
            className="text-xs"
            title={execution.startedAt ? new Date(execution.startedAt).toLocaleString() : undefined}
          >
            {formatTimestamp(execution.startedAt)}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground">Completed</div>
          <div
            className="text-xs"
            title={execution.completedAt ? new Date(execution.completedAt).toLocaleString() : undefined}
          >
            {formatTimestamp(execution.completedAt)}
          </div>
        </div>
      </div>

      {/* Error */}
      {execution.error && (
        <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm flex-shrink-0 mx-4 mb-4">
          {execution.error}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center border-b flex-shrink-0">
          <TabsList className="h-auto p-0 bg-transparent rounded-none">
            <TabsTrigger
              value="timeline"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2"
            >
              Timeline
            </TabsTrigger>
            <TabsTrigger
              value="waterfall"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2"
            >
              Waterfall
            </TabsTrigger>
            <TabsTrigger
              value="results"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2"
            >
              Results
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="flex-1 min-h-0 mt-0">
          {!logs || logs.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4">
              No logs yet
            </div>
          ) : (
            <div className="relative overflow-hidden h-full">
              <div className="w-full h-full">
                <ScrollArea ref={timelineScrollRef} className="h-full">
                  <Table>
                    <TableHeader className="bg-muted/50 sticky top-0">
                      <TableRow>
                        <TableHead className="w-32 px-3">Timestamp</TableHead>
                        <TableHead className="w-8 px-3"></TableHead>
                        <TableHead className="px-3">Stage</TableHead>
                        <TableHead className="px-3">Message</TableHead>
                        <TableHead className="w-20 px-3">Tier</TableHead>
                        <TableHead className="w-20 px-3">Status</TableHead>
                        <TableHead className="w-20 px-3">Duration</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log, i) => (
                        <TableRow
                          key={i}
                          className={`cursor-pointer ${selectedLogIndex === i ? 'bg-muted' : ''}`}
                          onClick={() => setSelectedLogIndex(selectedLogIndex === i ? null : i)}
                          data-state={selectedLogIndex === i ? 'selected' : undefined}
                        >
                          <TableCell className="px-3 py-2 text-muted-foreground text-xs whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </TableCell>
                          <TableCell className={`px-3 py-2 ${getStageColor(log.stage)}`}>
                            {getStageIcon(log.stage)}
                          </TableCell>
                          <TableCell className="px-3 py-2">
                            <Badge variant="outline" className="text-xs font-mono">
                              {log.stage}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-3 py-2 truncate max-w-[200px] text-sm" title={log.message}>
                            {log.message}
                          </TableCell>
                          <TableCell className="px-3 py-2">
                            {log.tier && (
                              <Badge variant="secondary" className="text-xs">
                                {log.tier}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="px-3 py-2">
                            {log.status && (
                              <Badge
                                variant={log.status >= 200 && log.status < 300 ? 'success' : 'destructive'}
                                className="text-xs"
                              >
                                {log.status}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="px-3 py-2">
                            {log.duration !== undefined && (
                              <Badge variant="outline" className="text-xs">
                                {log.duration}ms
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>

              {/* Log Details Panel - Overlay on right side */}
              {selectedLog && (
                <div className="absolute top-0 right-0 bottom-0 w-1/2 bg-background border-l shadow-lg z-10">
                  <ScrollArea className="h-full">
                    <div className="p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">Log Details</h4>
                        <button
                          onClick={() => setSelectedLogIndex(null)}
                          className="text-muted-foreground hover:text-foreground text-xs"
                        >
                          Close
                        </button>
                      </div>

                      <div className="space-y-3 text-sm">
                        <div>
                          <div className="text-muted-foreground text-xs mb-1">Stage</div>
                          <Badge variant="outline" className="font-mono">
                            {selectedLog.stage}
                          </Badge>
                        </div>

                        <div>
                          <div className="text-muted-foreground text-xs mb-1">Timestamp</div>
                          <div>{new Date(selectedLog.timestamp).toLocaleString()}</div>
                        </div>

                        <div>
                          <div className="text-muted-foreground text-xs mb-1">Message</div>
                          <div className="break-words">{selectedLog.message}</div>
                        </div>

                        {selectedLog.tier && (
                          <div>
                            <div className="text-muted-foreground text-xs mb-1">Tier</div>
                            <Badge variant="secondary">{selectedLog.tier}</Badge>
                          </div>
                        )}

                        {selectedLog.status && (
                          <div>
                            <div className="text-muted-foreground text-xs mb-1">HTTP Status</div>
                            <Badge
                              variant={selectedLog.status >= 200 && selectedLog.status < 300 ? 'success' : 'destructive'}
                            >
                              {selectedLog.status}
                            </Badge>
                          </div>
                        )}

                        {selectedLog.duration !== undefined && (
                          <div>
                            <div className="text-muted-foreground text-xs mb-1">Duration</div>
                            <div>{selectedLog.duration}ms</div>
                          </div>
                        )}

                        {selectedLog.error && (
                          <div>
                            <div className="text-muted-foreground text-xs mb-1">Error</div>
                            <div className="text-destructive break-words">{selectedLog.error}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Waterfall Tab */}
        <TabsContent value="waterfall" className="flex-1 min-h-0 mt-0">
          <ExecutionWaterfall logs={logs} execution={execution} />
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results" className="flex-1 min-h-0 mt-0">
          {!execution.requestResults ? (
            <div className="text-sm text-muted-foreground text-center py-4">
              No results yet
            </div>
          ) : (
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-end px-4 py-2 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={handleCopyResults} className="gap-2">
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <ScrollArea className="flex-1">
                <pre className="bg-muted p-4 mx-4 mb-4 rounded-md text-xs whitespace-pre-wrap break-all max-w-full">
                  {JSON.stringify(execution.requestResults, null, 2)}
                </pre>
              </ScrollArea>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Capture Details Component - uses store for logs
function CaptureDetails({ capture }: { capture: CaptureSessionWithAction }) {
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const logsScrollRef = useRef<HTMLDivElement>(null);
  const [selectedLogIndex, setSelectedLogIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string>('timeline');

  // Get logs from store (handles realtime vs stored logs automatically)
  const logs = useCaptureLogs(capture.id);

  const isCapturing = capture.status === 'capturing' || capture.status === 'pending';
  const selectedLog = selectedLogIndex !== null ? logs[selectedLogIndex] : null;
  const displayStatus = mapCaptureStatusForDisplay(capture.status);

  // Filter request logs for timeline tab (logs with URL/method details)
  const requestLogs = logs.filter(log => {
    const hasUrl = log.details?.url || log.details?.requestUrl;
    const hasMethod = log.details?.method;
    return hasUrl || hasMethod || log.stage.includes('request') || log.stage.includes('intercepted');
  });

  // Auto-scroll timeline when new request logs come in
  useEffect(() => {
    if (isCapturing && timelineScrollRef.current) {
      const viewport = timelineScrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        viewport.scrollTo({
          top: viewport.scrollHeight,
          behavior: 'smooth',
        });
      }
    }
  }, [requestLogs.length, isCapturing]);

  // Auto-scroll logs when new logs come in
  useEffect(() => {
    if (isCapturing && logsScrollRef.current) {
      const viewport = logsScrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        viewport.scrollTo({
          top: viewport.scrollHeight,
          behavior: 'smooth',
        });
      }
    }
  }, [logs.length, isCapturing]);

  // Reset selection when capture changes
  useEffect(() => {
    setSelectedLogIndex(null);
  }, [capture.id]);

  const getStageIcon = (stage: string) => {
    if (stage.includes('completed') || stage.includes('captured') || stage === 'pattern_matched') {
      return '✓';
    }
    if (stage.includes('failed') || stage === 'pattern_not_matched') {
      return '✕';
    }
    if (stage.includes('started') || stage.includes('opened') || stage.includes('set')) {
      return '●';
    }
    return '○';
  };

  const getStageColor = (stage: string) => {
    if (stage.includes('completed') || stage.includes('captured') || stage === 'pattern_matched') {
      return 'text-green-500';
    }
    if (stage.includes('failed') || stage === 'pattern_not_matched') {
      return 'text-destructive';
    }
    if (stage.includes('started') || stage.includes('opened') || stage.includes('set')) {
      return 'text-primary';
    }
    return 'text-muted-foreground';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Metadata */}
      <div className="grid grid-cols-2 gap-3 text-sm flex-shrink-0 pb-4">
        <div>
          <div className="text-muted-foreground">Status</div>
          <StatusBadge status={displayStatus as ExecutionStatus | 'idle'} />
        </div>
        <div>
          <div className="text-muted-foreground">Started</div>
          <div>{new Date(capture.startedAt).toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Completed</div>
          <div>{capture.completedAt ? new Date(capture.completedAt).toLocaleString() : '-'}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Duration</div>
          <div>
            {capture.completedAt && capture.startedAt
              ? `${Math.round((new Date(capture.completedAt).getTime() - new Date(capture.startedAt).getTime()) / 1000)}s`
              : '-'}
          </div>
        </div>
      </div>

      {/* Error */}
      {capture.error && (
        <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm flex-shrink-0 mb-4">
          {capture.error}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0 -mx-4">
        <div className="flex items-center border-b flex-shrink-0">
          <TabsList className="h-auto p-0 bg-transparent rounded-none">
            <TabsTrigger
              value="timeline"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2"
            >
              Timeline
            </TabsTrigger>
            <TabsTrigger
              value="logs"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2"
            >
              Logs
            </TabsTrigger>
          </TabsList>
          {isCapturing && (
            <Badge variant="outline" className="animate-pulse text-xs ml-2">Live</Badge>
          )}
        </div>

        {/* Timeline Tab - Request logs table */}
        <TabsContent value="timeline" className="flex-1 min-h-0 mt-0">
          {requestLogs.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4">
              No request logs yet
            </div>
          ) : (
            <div className="relative overflow-hidden h-full">
              <div className="w-full h-full">
                <ScrollArea ref={timelineScrollRef} className="h-full">
                  <Table>
                    <TableHeader className="bg-muted/50 sticky top-0">
                      <TableRow>
                        <TableHead className="w-40 px-3">Timestamp</TableHead>
                        <TableHead className="w-8 px-3"></TableHead>
                        <TableHead className="px-3">Stage</TableHead>
                        <TableHead className="w-20 px-3">Method</TableHead>
                        <TableHead className="px-3">URL</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requestLogs.map((log, i) => {
                        const method = log.details?.method as string | undefined;
                        const url = (log.details?.url || log.details?.requestUrl) as string | undefined;
                        const originalIndex = logs.indexOf(log);
                        return (
                          <TableRow
                            key={i}
                            className={`cursor-pointer ${selectedLogIndex === originalIndex ? 'bg-muted' : ''}`}
                            onClick={() => setSelectedLogIndex(selectedLogIndex === originalIndex ? null : originalIndex)}
                            data-state={selectedLogIndex === originalIndex ? 'selected' : undefined}
                          >
                            <TableCell className="px-3 py-2 text-muted-foreground text-xs whitespace-nowrap">
                              {new Date(log.timestamp).toLocaleString()}
                            </TableCell>
                            <TableCell className={`px-3 py-2 ${getStageColor(log.stage)}`}>
                              {getStageIcon(log.stage)}
                            </TableCell>
                            <TableCell className="px-3 py-2">
                              <Badge variant="outline" className="text-xs font-mono">
                                {log.stage}
                              </Badge>
                            </TableCell>
                            <TableCell className="px-3 py-2">
                              {method && (
                                <Badge variant={method === 'GET' ? 'secondary' : 'default'} className="text-xs font-mono">
                                  {method}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="px-3 py-2 truncate max-w-[300px] text-xs font-mono text-muted-foreground" title={url}>
                              {url || '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>

              {/* Log Details Panel - Overlay on right side */}
              {selectedLog && (
                <div className="absolute top-0 right-0 bottom-0 w-1/2 bg-background border-l shadow-lg z-10">
                  <ScrollArea className="h-full">
                    <div className="p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">Log Details</h4>
                        <button
                          onClick={() => setSelectedLogIndex(null)}
                          className="text-muted-foreground hover:text-foreground text-xs"
                        >
                          Close
                        </button>
                      </div>

                      <div className="space-y-3 text-sm">
                        <div>
                          <div className="text-muted-foreground text-xs mb-1">Stage</div>
                          <Badge variant="outline" className="font-mono">
                            {selectedLog.stage}
                          </Badge>
                        </div>

                        <div>
                          <div className="text-muted-foreground text-xs mb-1">Timestamp</div>
                          <div>{new Date(selectedLog.timestamp).toLocaleString()}</div>
                        </div>

                        <div>
                          <div className="text-muted-foreground text-xs mb-1">Message</div>
                          <div className="break-words">{selectedLog.message}</div>
                        </div>

                        {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                          <div>
                            <div className="text-muted-foreground text-xs mb-1">Details</div>
                            <pre className="bg-muted p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap break-all">
                              {JSON.stringify(selectedLog.details, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Logs Tab - Streaming full logs */}
        <TabsContent value="logs" className="flex-1 min-h-0 mt-0">
          {logs.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4">
              No logs yet
            </div>
          ) : (
            <div className="h-full">
              <ScrollArea ref={logsScrollRef} className="h-full">
                <div className="p-4 font-mono text-xs space-y-1">
                  {logs.map((log, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="text-muted-foreground whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                      <span className={`${getStageColor(log.stage)} flex-shrink-0`}>
                        [{log.stage}]
                      </span>
                      <span className="break-all">{log.message}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Execution Timeline Component
function ExecutionTimeline({ logs }: { logs: ExecutionLogEntry[] }) {
  const getStageStyle = (stage: string) => {
    if (stage.includes('completed')) return { icon: '✓', color: 'text-green-500 bg-green-500/15' };
    if (stage.includes('failed')) return { icon: '✕', color: 'text-destructive bg-destructive/15' };
    if (stage.includes('fallback')) return { icon: '↓', color: 'text-yellow-500 bg-yellow-500/15' };
    if (stage.includes('started')) return { icon: '●', color: 'text-primary bg-primary/15' };
    return { icon: '○', color: 'text-muted-foreground bg-muted' };
  };

  return (
    <div className="relative pl-6">
      <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-border" />
      <div className="space-y-2">
        {logs.map((log, i) => {
          const { icon, color } = getStageStyle(log.stage);
          return (
            <div key={i} className="relative flex items-start gap-2">
              <div className={`absolute -left-4 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${color}`}>
                {icon}
              </div>
              <div className="flex-grow text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span>{log.message}</span>
                  {log.tier && <Badge variant="secondary" className="text-xs">{log.tier}</Badge>}
                  {log.status && (
                    <Badge
                      variant={log.status >= 200 && log.status < 300 ? 'success' : 'destructive'}
                      className="text-xs"
                    >
                      {log.status}
                    </Badge>
                  )}
                  {log.duration !== undefined && (
                    <Badge variant="outline" className="text-xs">{log.duration}ms</Badge>
                  )}
                </div>
                {log.error && <div className="text-xs text-destructive mt-1">{log.error}</div>}
                <div className="text-xs text-muted-foreground">{new Date(log.timestamp).toLocaleTimeString()}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Capture Timeline Component
function CaptureTimeline({ logs }: { logs: CaptureLogEntry[] }) {
  const getStageStyle = (stage: string) => {
    if (stage.includes('completed') || stage.includes('captured') || stage === 'pattern_matched') {
      return { icon: '✓', color: 'text-green-500 bg-green-500/15' };
    }
    if (stage.includes('failed') || stage === 'pattern_not_matched') {
      return { icon: '✕', color: 'text-destructive bg-destructive/15' };
    }
    if (stage.includes('started') || stage.includes('opened') || stage.includes('set')) {
      return { icon: '●', color: 'text-primary bg-primary/15' };
    }
    if (stage.includes('intercepted')) {
      return { icon: '○', color: 'text-muted-foreground bg-muted' };
    }
    return { icon: '○', color: 'text-muted-foreground bg-muted' };
  };

  if (logs.length === 0) {
    return <div className="text-sm text-muted-foreground text-center py-4">No logs yet</div>;
  }

  return (
    <div className="relative pl-6">
      <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-border" />
      <div className="space-y-2">
        {logs.map((log, i) => {
          const { icon, color } = getStageStyle(log.stage);
          return (
            <div key={i} className="relative flex items-start gap-2">
              <div className={`absolute -left-4 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${color}`}>
                {icon}
              </div>
              <div className="flex-grow text-sm">
                <Badge variant="outline" className="text-xs font-mono mb-1">{log.stage}</Badge>
                <div>{log.message}</div>
                {log.details && Object.keys(log.details).length > 0 && (
                  <details className="text-xs text-muted-foreground mt-1">
                    <summary className="cursor-pointer">Details</summary>
                    <pre className="bg-muted p-2 rounded mt-1 overflow-x-auto">
                      {JSON.stringify(log.details, null, 2)}
                    </pre>
                  </details>
                )}
                <div className="text-xs text-muted-foreground">{new Date(log.timestamp).toLocaleTimeString()}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Helper: Format interval
function formatInterval(ms: number): string {
  if (ms < 60000) return `${ms / 1000}s`;
  if (ms < 3600000) return `${ms / 60000}m`;
  return `${ms / 3600000}h`;
}

// Helper: Format Unix timestamp to human-readable UTC+7
function formatTimestampToUTC7(timestamp: number): string {
  // Unix timestamps could be in seconds or milliseconds
  // If timestamp is less than a reasonable millisecond value, it's in seconds
  const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
  const date = new Date(ms);

  // Format to UTC+7 (Asia/Bangkok, Asia/Jakarta, etc.)
  return date.toLocaleString('en-GB', {
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// Helper: Format execution inputs, converting timestamp fields to readable format
function formatInputValue(key: string, value: unknown): string {
  const strValue = String(value);

  // Check if this looks like a timestamp field and is a number
  const isTimestampKey = /^(day_|date_|time_|start|end|created|updated|timestamp)/i.test(key) ||
    /_at$/i.test(key) || /_(start|end|time|date)$/i.test(key);

  if (isTimestampKey && typeof value === 'number') {
    return formatTimestampToUTC7(value);
  }

  return strValue;
}
